import React, { useRef, useState, useEffect } from "react";
import { Star, ChevronLeft, ChevronRight, ExternalLink, Quote } from "lucide-react";
import { Reveal } from "./shared";
import { GOOGLE, REVIEWS } from "../../lib/data";

const GoogleG = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"/>
    <path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"/>
    <path fill="#FBBC05" d="M11.69 28.18c-.44-1.32-.69-2.73-.69-4.18s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88l7.35-5.7z"/>
    <path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z"/>
  </svg>
);

const Stars = ({ n = 5, size = 15 }) => (
  <div style={{ display: "flex", gap: 2 }} aria-label={`${n} out of 5 stars`}>
    {[...Array(5)].map((_, i) => (
      <Star key={i} size={size} fill={i < n ? "#FBBC05" : "none"} stroke="#FBBC05" />
    ))}
  </div>
);

export const Reviews = () => {
  const trackRef = useRef(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  const updateArrows = () => {
    const el = trackRef.current;
    if (!el) return;
    setAtStart(el.scrollLeft <= 4);
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 4);
  };

  useEffect(() => {
    updateArrows();
    const el = trackRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateArrows, { passive: true });
    window.addEventListener("resize", updateArrows);
    return () => {
      el.removeEventListener("scroll", updateArrows);
      window.removeEventListener("resize", updateArrows);
    };
  }, []);

  const scrollByCard = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const card = el.querySelector("[data-review-card]");
    const amount = card ? card.offsetWidth + 22 : 360;
    el.scrollBy({ left: dir * amount, behavior: "smooth" });
  };

  return (
    <section className="section alt" id="reviews" data-testid="reviews-section">
      <div className="container">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 28 }}>
          <div>
            <Reveal><p className="eyebrow">Reviews</p></Reveal>
            <Reveal delay={0.05}>
              <h2 className="section-title" style={{ marginTop: 16 }}>Loved on <span className="accent">Google</span></h2>
            </Reveal>
          </div>

          <Reveal delay={0.1}>
            <div style={{ display: "flex", alignItems: "center", gap: 22, background: "#fff", borderRadius: 18,
              padding: "18px 24px", boxShadow: "var(--shadow-soft)", border: "1px solid rgba(17,17,17,0.05)" }}
              data-testid="google-rating-header">
              <GoogleG size={30} />
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontFamily: "'Bebas Neue'", fontSize: 40, lineHeight: 1, color: "var(--black)" }}>{GOOGLE.rating.toFixed(1)}</span>
                  <div>
                    <Stars n={Math.round(GOOGLE.rating)} size={16} />
                    <span style={{ fontSize: 12.5, color: "var(--muted)" }}>Based on {GOOGLE.count} reviews</span>
                  </div>
                </div>
              </div>
              <a href={GOOGLE.url} target="_blank" rel="noopener noreferrer" className="btn btn-ghost-dark"
                data-testid="google-reviews-link" style={{ padding: "12px 20px", fontSize: 12 }}>
                Read on Google <ExternalLink size={14} />
              </a>
            </div>
          </Reveal>
        </div>

        {/* Carousel */}
        <div style={{ position: "relative", marginTop: 44 }}>
          <div ref={trackRef} data-testid="reviews-track" className="reviews-track"
            style={{ display: "flex", gap: 22, overflowX: "auto", scrollSnapType: "x mandatory",
              paddingBottom: 12, scrollbarWidth: "none" }}>
            {REVIEWS.map((r, i) => (
              <div key={r.name + i} data-review-card data-testid={`review-card-${i}`}
                style={{ scrollSnapAlign: "start", flex: "0 0 auto", width: "min(360px, 82vw)",
                  background: "#fff", borderRadius: "var(--radius-lg)", padding: "30px 28px",
                  boxShadow: "var(--shadow-soft)", border: "1px solid rgba(17,17,17,0.05)",
                  display: "flex", flexDirection: "column" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <Stars n={r.rating} />
                  <GoogleG size={20} />
                </div>
                <Quote size={26} style={{ color: "var(--gold)", margin: "16px 0 6px", opacity: 0.5 }} />
                <p style={{ fontSize: 15, color: "var(--text)", lineHeight: 1.65, flex: 1 }}>{r.text}</p>
                <div style={{ display: "flex", alignItems: "center", gap: 13, marginTop: 22 }}>
                  <div style={{ width: 44, height: 44, borderRadius: "50%",
                    background: "linear-gradient(135deg,var(--red-light),var(--red-dark))",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: "var(--gold)", fontFamily: "'Bebas Neue'", fontSize: 20, flex: "none" }}>{r.initial}</div>
                  <div>
                    <strong style={{ display: "block", fontSize: 14.5, color: "var(--black)" }}>{r.name}</strong>
                    <span style={{ fontSize: 12.5, color: "var(--muted)" }}>{r.ago}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div style={{ display: "flex", gap: 12, marginTop: 20 }}>
            <button onClick={() => scrollByCard(-1)} disabled={atStart} data-testid="reviews-prev"
              aria-label="Previous reviews" className="review-arrow"
              style={{ opacity: atStart ? 0.35 : 1, cursor: atStart ? "default" : "pointer" }}>
              <ChevronLeft size={20} />
            </button>
            <button onClick={() => scrollByCard(1)} disabled={atEnd} data-testid="reviews-next"
              aria-label="Next reviews" className="review-arrow"
              style={{ opacity: atEnd ? 0.35 : 1, cursor: atEnd ? "default" : "pointer" }}>
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};
